import { z } from 'zod';
import { networkSchematicSchema } from '../inspection/network-schematic.js';
import { inquiryListRowSchema } from '../inquiry/inquiry-list.js';
import { sewerNetworkTypeSchema } from '../inquiry/sewer-network-type.js';
import { reportSummarySchema } from '../inspection/report.js';
import { currentInspectionProposalSchema, inspectionProposalV1Schema, PROPOSAL_PAYLOAD_VERSION, upgradeProposal, } from './inspection-submission.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * Une proposition telle que le back-office la **relit** — le pendant en lecture de
 * `inspectionSubmissionSchema`, qui décrit le dépôt.
 *
 * ⚠️ **C'est une proposition, pas une `Inspection`.** La table `Inspection` (M16 → M20) n'existe
 * pas ; ce que le bureau lit est la ligne `proposal` déposée par le terrain (D4). C'est ce qui
 * garde cette lecture en lecture seule : `Visit`, `Inspection` et `Document` naîtront à la
 * validation, et rien ici ne les anticipe.
 *
 * Les deux instants sont **distincts et tous les deux rendus** : `submittedAt` est celui où la
 * tablette a clos la fiche, `receivedAt` celui où l'API l'a reçue. Une transmission peut attendre
 * des heures dans l'outbox, et les confondre effacerait exactement ce que la synchro différée
 * coûte — ce que le bureau a besoin de voir quand il relit une saisie de la semaine passée.
 *
 * L'équipe porte son nom, pas seulement son `id` : la fiche annonce « transmis par JL·TB »
 * (verbatim — M22), et un uuid n'a rien à dire à un gestionnaire.
 *
 * ⚠️ Ce que cette forme ne porte pas : **l'auteur de la saisie**. `proposal.user_id` est nullable
 * — nul pour un compte d'équipe partagé (M6) —, et #58 ne demande que le relevé, les pièces et la
 * signature. L'exposer supposerait de trancher ce qu'on affiche quand il est nul, ce qui est une
 * question d'écran, pas de lecture.
 */
export const submittedInspectionSchema = z.object({
    id: uuidV7Schema,
    submittedAt: z.iso.datetime({ offset: true }),
    receivedAt: z.iso.datetime({ offset: true }),
    team: z.object({
        id: uuidV7Schema,
        name: z.string(),
    }),
    /** Déjà converti à la version courante du relevé — voir `readProposalPayload`. */
    payload: currentInspectionProposalSchema,
});
/**
 * Toutes les propositions d'une demande, **la plus récente d'abord**.
 *
 * ⚠️ **Une liste, jamais un objet, et c'est le cœur de ce contrat.** Une demande peut en porter
 * plusieurs — deux tablettes, un brouillon reparti de zéro, une reprise après renvoi au terrain
 * (`MAX_PROPOSALS_PER_INQUIRY` vaut 20, et sa fiche dit pourquoi). Ne rendre que la dernière
 * priverait l'écran du seul moyen de dire « il y en a deux » : le gestionnaire lirait une fiche
 * comme *la* vérité sans rien pour l'avertir qu'une autre saisie existe. La table le dit
 * elle-même — « deux propositions divergentes sont deux entrées dans une file, pas un conflit ».
 *
 * ⚠️ `total` compte **toutes** les propositions de la demande, `items` celles qui ont pu être
 * relues. Les deux divergent quand une charge utile est illisible : elle est journalisée et
 * retirée d'`items`, et l'écart est ce qui permet à l'écran de le dire au lieu d'afficher une
 * fiche muette. Même discipline que `total` face à `items.length` partout ailleurs (D24).
 *
 * Pas de pagination : le plafond est à vingt, et paginer une fiche de lecture n'aurait pas de
 * sens — on l'ouvre pour tout voir.
 */
export const submittedInspectionsSchema = z.object({
    /**
     * La demande dont on lit les contrôles — l'adresse, les deux références et le statut, que la
     * charge utile ne porte pas : le terrain n'a aucune raison de renvoyer ce qu'il a reçu.
     *
     * ⚠️ **Même projection que la ligne du tableau** (`inquiryListRowSchema`), et volontairement :
     * l'en-tête de la fiche affiche exactement ce que la ligne affichait. Une seconde forme pour
     * les mêmes six champs, c'est un second aplatissement d'adresse à tenir — ce que le dépôt
     * évite déjà en ne joignant l'adresse qu'à un seul endroit.
     *
     * `null` quand l'identifiant ne correspond à aucune demande : l'écran le dit, plutôt que de
     * rendre une fiche sans en-tête. C'est aussi le seul cas où `total` vaut zéro pour une raison
     * autre que « rien n'a encore été transmis », et les deux se lisent différemment.
     *
     * ⚠️ Son `team` est l'équipe **affectée** à la demande, celle de chaque proposition est celle
     * qui a **transmis**. Les deux diffèrent dès qu'une réaffectation est passée par là, et c'est
     * précisément ce qu'un gestionnaire a besoin de voir.
     */
    inquiry: inquiryListRowSchema.nullable(),
    /**
     * Le rendez-vous **en vigueur** sur la demande — le plus tardif quand un report en a posé
     * plusieurs (M24) —, ou `null` quand aucun n'est pris.
     *
     * ⚠️ Il est **hors des `items`, et c'est le fond du sujet** : le rendez-vous appartient à la
     * demande, la date de réalisation à chaque proposition. Le ranger dans une proposition ferait
     * croire que le terrain l'a transmis — il ne le connaît que parce que le bureau l'a posé — et
     * le recopierait autant de fois qu'il y a de saisies. C'est leur rapprochement, ici, qui rend
     * l'écart visible (#115) ; l'écran le montre, il ne le corrige pas (M21).
     */
    appointment: z
        .object({ id: uuidV7Schema, scheduledAt: z.iso.datetime({ offset: true }) })
        .nullable(),
    /**
     * Le rapport PDF du contrôle validé le plus récent de la demande, ou `null` tant qu'aucun n'a été
     * généré ([M11](../../../../docs/MODELE-DONNEES.md#m11)).
     *
     * ⚠️ **Hors des `items`, pour la raison du rendez-vous ci-dessus** : un rapport est rattaché à un
     * contrôle *matérialisé*, pas à une proposition. Les `items` sont des saisies ; un rapport est
     * ce qu'une saisie validée est devenue.
     *
     * ⚠️ `null` dit seulement « rien à télécharger ». Le *pourquoi* se lit à côté : le statut de la
     * demande dit qu'une demande jamais validée n'en a pas encore, et `reportStalled` sépare la
     * génération en cours de celle qui n'aura pas lieu.
     */
    report: reportSummarySchema.nullable(),
    /**
     * Vrai quand le **dernier contrôle** de la demande n'a pas de rapport **et qu'aucune génération
     * n'est en file ni en cours** : le job est parti au rebut, ou n'a jamais été publié — file
     * indisponible au moment de la validation. Rien ne le reprendra seul ; c'est ce qui arme la
     * relance (`POST validation/inquiries/:inquiryId/report`).
     *
     * ⚠️ **Pas un second statut** (M3) : rien ne le stocke. Il se lit dans la file à chaque lecture,
     * et redevient `false` dès qu'une relance y est.
     */
    reportStalled: z.boolean(),
    /**
     * La **parcelle** du lieu, sous la forme que le rapport imprime (`displayCadastralReference`) :
     * celle du dossier (`Site.cadastral_reference`, M30), sinon celle que la réf. GHP porte —
     * `null` quand ni l'une ni l'autre. Le valideur relit ce qui partira au client dans un document
     * jamais régénéré.
     *
     * ⚠️ Hors de `inquiry`, qui est la projection de la ligne du tableau : la parcelle ne s'y
     * affiche pas, et la servir à chaque ligne de chaque page ne coûterait que pour cet écran-ci.
     */
    parcel: z.string().nullable(),
    /**
     * Le **réseau public du dossier** (`Site.sewer_network_type`, M43), à côté de la parcelle et pour
     * la même raison : le valideur le relit à côté du réseau **constaté** (`survey.plot`), que l'agent
     * a relevé sur place. `null` quand le dossier ne le dit pas.
     *
     * ⚠️ Il **annonce**, il ne pré-remplit rien : seul le réseau constaté décide des anomalies.
     * `null` par défaut : une API d'avant ce champ ne fait pas tomber la fiche.
     */
    sewerNetworkType: sewerNetworkTypeSchema.nullable().default(null),
    /**
     * La proposition **la plus récente, lisible ou non** — celle que la décision compare
     * (`proposal_superseded`) —, `null` quand rien n'a été transmis.
     *
     * ⚠️ Hors des `items`, et c'est son rôle : une saisie illisible en est retirée, et quand c'est la
     * seule, la fiche n'avait aucun jeton pour « Renvoyer au terrain » — alors que la validation
     * refusée dit précisément de renvoyer. Le renvoi l'accepte : il ne relit pas la charge utile.
     */
    latestProposalId: uuidV7Schema.nullable(),
    items: z.array(submittedInspectionSchema),
    total: z.number().int().min(0),
    /**
     * Le schéma corrigé au bureau (#437), sur la saisie `proposalId` — `null` sans correction. La
     * saisie de l'agent reste lisible à côté (D4) ; la validation fera de celui-ci le schéma du rapport.
     */
    officeSchematic: z
        .object({ proposalId: uuidV7Schema, schematic: networkSchematicSchema })
        .nullable()
        .default(null),
});
/** Ce que le bureau envoie avec l'image du schéma corrigé (#437). */
export const correctSchematicSchema = z.strictObject({
    proposalId: uuidV7Schema,
    schematic: networkSchematicSchema,
});
/**
 * Levée quand une charge utile stockée ne peut pas être ramenée à la version courante.
 *
 * Nommée plutôt qu'anonyme : l'appelant doit pouvoir la distinguer d'une panne pour la
 * journaliser et **continuer** — une proposition illisible n'empêche pas de lire les autres.
 */
export class UnreadableProposalError extends Error {
    payloadVersion;
    constructor(payloadVersion, cause) {
        super(`charge utile de version ${payloadVersion} illisible`, { cause });
        this.payloadVersion = payloadVersion;
        this.name = 'UnreadableProposalError';
    }
}
/**
 * **La couture de version, à la lecture** — et le seul endroit où une charge utile stockée devient
 * un objet typé.
 *
 * `proposal.payload` est délibérément `unknown` en base : c'est `payload_version` qui organise la
 * coexistence de plusieurs versions, et la montée de version est une fonction pure appliquée
 * **ici**. Typer la colonne v1 court-circuiterait la conversion — un lecteur recevrait une ligne
 * v2 déjà typée v1, sans un mot du compilateur.
 *
 * ⚠️ Elle rend **toujours la version courante du relevé** : une charge utile déposée en
 * `survey_v1` est validée contre la v1 puis montée en v2 (`upgradeProposal`). C'est ce qui
 * permet à un écran de lire une fiche de juillet et une fiche d'aujourd'hui avec le même code —
 * et à un contrôle déjà transmis de se relire tel qu'il a été relevé. La seule valeur que la
 * montée ne rend pas est la case `COMCOM` de la v1, qui n'en était pas une (`upgradeSurvey`).
 *
 * ⚠️ Une version inconnue **lève**, elle ne se devine pas. C'est le seul refus de ce lot, et il
 * relève de l'intégrité structurelle : rendre une v2 comme si c'était une v1 afficherait des
 * champs faux avec l'aplomb des vrais, ce que M21 interdit plus sûrement encore que de refuser.
 */
export function readProposalPayload(payloadVersion, payload) {
    if (payloadVersion !== PROPOSAL_PAYLOAD_VERSION) {
        throw new UnreadableProposalError(payloadVersion);
    }
    const parsed = inspectionProposalV1Schema.safeParse(payload);
    if (!parsed.success)
        throw new UnreadableProposalError(payloadVersion, parsed.error);
    // ⚠️ Entrer en v1 ne garantit **pas** de sortir en v2, et c'est le seul endroit qui le sait :
    // les deux schémas sont `loose`, donc une v1 a pu ranger sous une clé que la v1 ignorait une
    // valeur que la v2 refuse. La montée qui échoue est une charge utile illisible comme une autre
    // — une de plus dans l'écart `total`/`items`, pas une panne du service.
    const upgraded = upgradeProposal(parsed.data);
    if (upgraded === null)
        throw new UnreadableProposalError(payloadVersion);
    return upgraded;
}
/**
 * La charge utile référence-t-elle ce média ? Lu **sans passer par la validation**, et c'est
 * délibéré.
 *
 * ⚠️ C'est ce qui autorise le bureau à relire une pièce d'une proposition par ailleurs illisible.
 * Les octets d'une photo de terrain ne se reconstituent pas (M21) : les refuser parce qu'une
 * anomalie de la même fiche a perdu sa phrase serait détruire ce qu'on a pour punir ce qu'on n'a
 * pas.
 *
 * ⚠️ Et c'est **la clé de l'autorisation**, pas une commodité. La clé de stockage est
 * `${teamId}/${mediaId}` : l'équipe vient de la ligne `proposal`, jamais de la requête. Une
 * lecture ne peut donc atteindre que les médias qu'une proposition de la demande demandée
 * référence — un identifiant deviné ne mène nulle part, et surtout pas chez une autre équipe.
 *
 * La signature est traitée comme les autres : elle **est** un média (`type: 'signature'`), et
 * `signatureMediaId` la désigne. Les deux chemins sont testés parce que le second existe seul
 * dans les fiches où la tablette n'a pas rangé la signature dans `media`.
 */
export function proposalReferencesMedium(payload, mediaId) {
    if (typeof payload !== 'object' || payload === null)
        return false;
    const { media, signatureMediaId } = payload;
    if (signatureMediaId === mediaId)
        return true;
    if (!Array.isArray(media))
        return false;
    return media.some((medium) => typeof medium === 'object' && medium !== null && medium.id === mediaId);
}
//# sourceMappingURL=submitted-inspection.js.map